<template>
  <v-content class="client opportunities list page">
    <v-progress-linear
      v-if="loading"
      :color="themes.client.light.input"
      indeterminate
      class="my-0"
    />
    <v-container v-else>
      <v-layout column>
        <v-toolbar flat color="transparent" class="mb-4">
          <v-toolbar-title>Opportunities</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            :color="themes.client.light.input"
            prepend-icon="mdi-magnify"
            placeholder="Search"
            class="pb-3"
            hide-details
            single-line
          />
        </v-toolbar>

        <div v-for="group in yearGroups" :key="`group-${group}`" class="mb-4">
          <div>
            <v-avatar
              :size="48"
              style="position: absolute; margin-top: -4px; z-index: 1"
            >
              <v-img
                :src="
                  organization.logo ||
                  '/img/missing/logos/organizations/original.png'
                "
              />
            </v-avatar>
            <v-chip color="white">
              <v-avatar :size="48"> </v-avatar>
              <span class="title pl-2">{{ group }}</span>
            </v-chip>
          </div>
          <div
            v-for="(opportunity, i) in groupedOpportunities[group]"
            :key="`opportunity-${opportunity.id}`"
          >
            <v-card class="my-2 ml-4">
              <v-card-title
                @click="
                  $router.push(
                    `/client/opportunities/${opportunity.id}/applications`
                  )
                "
                style="cursor: pointer"
              >
                <v-layout>
                  <div class="subheading truncate">
                    <div class="mr-2">
                      <v-tooltip bottom>
                        <span class="fade-out-right pr-4" slot="activator">
                          {{ opportunity.name }}
                        </span>
                        <span> {{ opportunity.name }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                  <v-spacer />
                  <div class="d-flex text-no-wrap" style="z-index: 1">
                    <v-tooltip
                      v-if="
                        new Date(opportunity.opensAt) < new Date() &&
                        new Date(opportunity.closesAt) > new Date()
                      "
                      :key="`open-${i}`"
                      bottom
                    >
                      <span class="pr-4" slot="activator">
                        <v-icon :color="themes.client.light.success">
                          mdi-check-circle
                        </v-icon>
                        <span
                          class="caption"
                          :class="themes.client.light.mutedText"
                        >
                          Open&nbsp;&nbsp;&nbsp;
                        </span>
                      </span>
                      Application open
                      {{ new Date(opportunity.opensAt) | date("d MMM ''yy") }} -
                      {{ new Date(opportunity.closesAt) | date("d MMM ''yy") }}
                    </v-tooltip>
                    <v-tooltip v-else :key="`close-${i}`" bottom>
                      <span class="pr-4" slot="activator">
                        <v-icon :color="themes.client.light.muted"
                          >mdi-close-circle</v-icon
                        >
                        <span
                          class="caption"
                          :class="themes.client.light.mutedText"
                        >
                          Closed
                        </span>
                      </span>
                      Application opened
                      {{ new Date(opportunity.opensAt) | date("d MMM ''yy") }} -
                      {{ new Date(opportunity.closesAt) | date("d MMM ''yy") }}
                    </v-tooltip>
                    <v-tooltip
                      v-if="isActive(opportunity)"
                      :key="`published-${i}`"
                      bottom
                    >
                      <span class="pr-4" slot="activator">
                        <v-icon :color="themes.client.light.success">
                          mdi-check-circle
                        </v-icon>
                        <span
                          class="caption"
                          :class="themes.client.light.mutedText"
                        >
                          Active&nbsp;
                        </span>
                      </span>
                      Opportunity activated
                      {{
                        new Date(opportunity.publishedAt) | date("d MMM ''yy")
                      }}
                    </v-tooltip>
                    <v-tooltip v-else :key="`unpublished-${i}`" bottom>
                      <span class="pr-3" slot="activator">
                        <v-icon :color="themes.client.light.muted"
                          >mdi-close-circle</v-icon
                        >
                        <span
                          class="caption"
                          :class="themes.client.light.mutedText"
                        >
                          Disabled
                        </span>
                      </span>
                      Opportunity not publicly visible
                    </v-tooltip>
                    <v-tooltip bottom v-if="opportunity.applicationsCount >= 0">
                      <span slot="activator" class="pr-3">
                        <v-icon :color="themes.client.light.muted"
                          >mdi-account-group</v-icon
                        >
                        <span
                          class="caption"
                          :class="themes.client.light.mutedText"
                        >
                          <span
                            v-for="i in Math.max(
                              0,
                              3 - `${opportunity.applicationsCount}`.length
                            )"
                            :key="i"
                          >
                            &nbsp;
                          </span>
                          {{ opportunity.applicationsCount }}
                        </span>
                      </span>
                      <span>
                        {{ opportunity.applicationsCount }}
                        {{
                          opportunity.applicationsCount
                            | pluralize("Application")
                        }}
                      </span>
                    </v-tooltip>
                    <v-tooltip bottom v-if="opportunity.locations">
                      <span slot="activator" class="pr-3">
                        <v-icon :color="themes.client.light.muted"
                          >mdi-map-marker</v-icon
                        >
                        <span
                          class="caption"
                          :class="themes.client.light.mutedText"
                        >
                          {{
                            `${"\xa0"}${"\xa0"}${
                              opportunity.locations.length
                            }`.slice(-2)
                          }}
                        </span>
                      </span>
                      <span>
                        {{ opportunity.locations.length }}
                        Opportunity
                        {{
                          opportunity.locations.length | pluralize("Location")
                        }}
                      </span>
                    </v-tooltip>
                    <v-menu offset-y left open-on-hover>
                      <span slot="activator" class="pr-3">
                        <v-icon
                          right
                          :color="themes.client.light.muted"
                          @click.stop=""
                        >
                          mdi-dots-vertical
                        </v-icon>
                      </span>
                      <v-list>
                        <v-list-tile
                          @click="
                            $router.push(`/opportunities/${opportunity.id}`)
                          "
                        >
                          <v-icon left>mdi-eye</v-icon>
                          View
                        </v-list-tile>
                        <v-list-tile
                          @click="
                            $router.push(
                              `/client/analytics/explorer?opportunity=${opportunity.id}`
                            )
                          "
                        >
                          <v-icon left>mdi-finance</v-icon>
                          Analytics
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </div>
                </v-layout>
              </v-card-title>
            </v-card>
          </div>
        </div>
        <div v-if="!opportunities[0]" class="ml-4">
          <div class="pl-2">No opportunities to display.</div>

          <div class="py-3" v-if="!user.primary">
            <v-btn
              :color="themes.client.light.primaryActionButton"
              @click="$router.push('/client/shared_candidates')"
            >
              <v-icon left>mdi-share-variant-outline</v-icon>
              View Shared Profiles
            </v-btn>
          </div>
        </div>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from "@/mixins/managedDocumentAttributes";

export default {
  mixins: [managedDocumentAttributes],

  created() {
    Promise.all([
      this.$store.dispatch("client/opportunities/fetchAll"),
      this.$store.dispatch("client/organization/fetch"),
    ])
      .then(() => {
        if (this.managedOpportunities && this.managedOpportunities.length > 0) {
          this.documentAttributes.title = `Opportunities – ${this.organization.name} – Leaply`;
          this.loading = false;
        } else {
          this.$router.push("/client/shared_candidates");
        }
      })
      .catch(() => {});
  },

  data() {
    return {
      loading: true,
      search: "",
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    opportunities() {
      return this.$store.getters["client/opportunities/all"];
    },
    managedOpportunities() {
      if (this.user.primary) return this.opportunities;

      return this.opportunities.filter((o) =>
        o.managers.some((m) => m.id === this.user.id)
      );
    },
    organization() {
      return this.$store.getters["client/organization/model"];
    },
    orderedOpportunities() {
      if (!this.managedOpportunities) return [];

      const opportunities = JSON.parse(
        JSON.stringify(this.managedOpportunities)
      );

      return opportunities
        .filter((o) => {
          const pattern = this.search.replace(/[^a-zA-Z0-9\s]/g, "");
          return RegExp(pattern, "gi").test(`${o.name}`);
        })
        .sort(({ opensAt: a }, { opensAt: b }) => {
          if (new Date(a) > new Date(b)) return -1;
          if (new Date(a) < new Date(b)) return 1;
          return 0;
        });
    },

    groupedOpportunities() {
      if (!this.orderedOpportunities[0]) return [];

      return _.groupBy(this.orderedOpportunities, (o) =>
        o.opensAt.substring(0, 4)
      );
    },

    yearGroups() {
      return Object.keys(this.groupedOpportunities).sort((a, b) => b - a);
    },
  },
  methods: {
    isActive(opportunity) {
      const uncommenced =
        !opportunity.commencesAt ||
        new Date(opportunity.commencesAt) > new Date();
      const published =
        opportunity.publishedAt &&
        new Date(opportunity.publishedAt) < new Date();
      return !!(published && uncommenced);
    },
  },
};
</script>

<style lang="scss">
.client.opportunities.list.page .truncate {
  max-width: 50%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .fade-out-right {
    &::after {
      content: " ";
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 50%;

      background: linear-gradient(to right, transparent 90%, #fff);
    }
  }
}
</style>
